// src/tabs/messages/SmsSendCard.jsx — LOT 5 : carte de configuration d'un
// ENVOI Plateformes canal SMS (message_sends canal='sms').
//
// POURQUOI UN COMPOSANT À PART : `InlineSendCard` (MessageEditor.jsx) n'est pas
// exporté, écrit en DIRECT dans Supabase (aucune validation SMS possible) et
// MessageEditor.jsx est une zone INTERDITE de ce lot. On ré-implémente donc
// localement la carte, en RÉUTILISANT (import, jamais copie) toutes les briques
// déjà exportées par MessageEditor.jsx : TRIGGER_TYPES, splitOffset/joinOffset,
// PLATFORM_OPTIONS, deriveSendConditionState/buildSendConditions,
// SEND_STATUS_OPTIONS… → un send SMS parle EXACTEMENT le même modèle de données
// qu'un send email (0 divergence de sérialisation des conditions).
//
// TROIS DIFFÉRENCES ASSUMÉES avec la carte Plateformes :
//   1. ÉCRITURE : jamais `supabase.from('message_sends')` — uniquement
//      `saveSmsSend` / `deleteSmsSend` (action serveur validée). C'est TOUT
//      l'objet du lot ;
//   2. TEXTE : la carte porte le corps du SMS (il vit dans `messages.body_fr`),
//      avec compteur PIRE CAS + segments + branche la plus longue à l'écran ;
//   3. PLATEFORME : « Direct » n'est JAMAIS proposé (ADR-266 strict côté SMS —
//      pas même l'exception « condition héritée » de l'onglet Plateformes).
//
// RGPD : aucune donnée voyageur ici (ni téléphone, ni nom) — que du gabarit.

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  MessageSquareText,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { DS } from "../../lib/ui";
import { useIsMobile } from "../../lib/useIsMobile";
import {
  computeWorstCase,
  validateSmsSendBody,
  smsSendCautionAirbnbConflictReason,
  smsSendCautionAirbnbMessage,
  SMS_SEND_PSEUDO_SLUG,
  SMS_SEND_RULES,
  SMS_SEND_VAR_MAX_LEN,
  SMS_SEGMENT_MAX,
} from "../../lib/sms-validator";
import { saveSmsSend, deleteSmsSend } from "../../lib/sms-sends-api";
// Libellés des conditions {#if} (dico des variables) — pour nommer le cas le
// plus long du compteur sans afficher les identifiants bruts.
import { ifOperandPhrase } from "./segment-editor/variable-labels";
// Briques RÉUTILISÉES telles quelles (MessageEditor.jsx n'est PAS modifié).
import {
  TRIGGER_TYPES,
  TEMPORAL_ANCHOR_PHRASE,
  OFFSET_UNIT_LABEL,
  PLATFORM_OPTIONS,
  splitOffset,
  joinOffset,
  deriveSendConditionState,
  buildSendConditions,
  SEND_STATUS_OPTIONS,
  sendStatusOptionKey,
  sendStatusOptionPatch,
  sendStatusHelp,
} from "./MessageEditor";

// D3 — un SMS d'instructions doit rester « Essentiel » (rattrapable) : un
// nouveau send démarre en Brouillon + catchup_mode 'full', jamais 'reminder'.
export const NEW_SMS_SEND_DEFAULTS = {
  send_id: null,
  name: "Envoi SMS",
  body_fr: "",
  trigger_type: "temporal_checkin",
  trigger_offset_value: -1,
  trigger_offset_unit: "days",
  trigger_time: "20:00",
  trigger_fixed_date: null,
  conditions: [],
  active_logement_ids: [],
  status: "draft",
  catchup_mode: "full",
};

// Variables et blocs proposés à l'insertion — SOURCE UNIQUE : la whitelist du
// validateur (SMS_SEND_RULES). Si la whitelist bouge, les boutons suivent.
const VAR_LABELS = {
  guest_first_name: "prénom du voyageur",
  lien_sejour: "lien de l'espace séjour",
};
const CONDITION_LABELS = {
  is_booking: "seulement Booking",
  is_airbnb: "seulement Airbnb",
  caution_manquante_effective: "si la caution manque",
  formulaire_manquant: "si le formulaire manque",
};

/** Convertit une ligne message_sends (avec messages joint) en état de carte. */
export function sendRowToDraft(row) {
  if (!row) return { ...NEW_SMS_SEND_DEFAULTS };
  const msg = row.messages || {};
  return {
    send_id: row.id,
    name: msg.name || "Envoi SMS",
    body_fr: msg.body_fr || "",
    trigger_type: row.trigger_type || "temporal_checkin",
    trigger_offset_value: Number.isFinite(row.trigger_offset_value)
      ? row.trigger_offset_value
      : 0,
    trigger_offset_unit: row.trigger_offset_unit || "days",
    trigger_time: row.trigger_time || "",
    trigger_fixed_date: row.trigger_fixed_date || null,
    conditions: Array.isArray(row.conditions) ? row.conditions : [],
    active_logement_ids: Array.isArray(row.active_logement_ids)
      ? row.active_logement_ids
      : [],
    status: row.status || "draft",
    catchup_mode: row.catchup_mode === "reminder" ? "reminder" : "full",
  };
}

// ── Compteur PIRE CAS d'un corps de send SMS ────────────────────────────────
// Demande explicite Dimitri : voir à l'écran le NOMBRE DE SEGMENTS et le compte
// de caractères pire cas, pour pouvoir raccourcir soi-même. La branche la plus
// longue est nommée (« sur la branche [is_booking + caution_manquante…] ») —
// sinon un dépassement causé par un seul bloc {#if} serait incompréhensible.
export function SmsSendWorstCase({ body, testId }) {
  const wc = computeWorstCase(SMS_SEND_PSEUDO_SLUG, body);
  const linkLen = SMS_SEND_VAR_MAX_LEN.lien_sejour;
  let text;
  let color = DS.textMuted;
  if (!wc.gsm7) {
    text = "caractère refusé par les SMS — voir le détail ci-dessous";
    color = DS.dangerText;
  } else {
    const seg = wc.segments || 0;
    text = `jusqu'à ${wc.length}/${SMS_SEGMENT_MAX} caractères · ${seg} SMS facturé${seg > 1 ? "s" : ""}`;
    const branch = wc.worstBranch && wc.worstBranch.conditions;
    if (branch && branch.length > 0)
      text += ` · cas le plus long : ${branch
        .map((c) => ifOperandPhrase(c).toLowerCase())
        .join(" + ")}`;
    if (wc.length > SMS_SEGMENT_MAX) color = DS.warningText;
  }
  return (
    <div data-testid={testId} style={{ marginTop: 5 }}>
      <div style={{ fontSize: 11, color, textAlign: "right", fontWeight: 600 }}>
        {text}
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: DS.textMuted,
          textAlign: "right",
          marginTop: 2,
          lineHeight: 1.4,
        }}
      >
        Le lien est compté {linkLen} caractères et le domaine est figé — pour
        raccourcir, c'est le TEXTE qu'on raccourcit.
      </div>
    </div>
  );
}

// Encart erreurs (bloquantes) / avertissements — mêmes styles que la vue SMS.
function Feedback({ errors, warnings, testId }) {
  if ((!errors || errors.length === 0) && (!warnings || warnings.length === 0))
    return null;
  return (
    <div data-testid={testId}>
      {errors && errors.length > 0 && (
        <div
          style={{
            background: DS.dangerBg,
            color: DS.dangerText,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: DS.radiusControl,
            padding: "9px 12px",
            marginTop: 9,
            fontSize: 12.5,
            lineHeight: 1.45,
          }}
        >
          {errors.map((e, i) => (
            <div key={`e-${i}`}>{e.message}</div>
          ))}
        </div>
      )}
      {warnings && warnings.length > 0 && (
        <div
          style={{
            marginTop: 9,
            border: `1px solid ${DS.warning}`,
            background: DS.warningBg,
            color: DS.warningText,
            borderRadius: 8,
            padding: "8px 11px",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.45,
          }}
        >
          {warnings.map((w, i) => (
            <div key={`w-${i}`}>{w.message}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlockTitle({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        letterSpacing: 0.5,
        color: DS.marine,
        fontWeight: 700,
        marginBottom: 6,
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function Chip({ on, onClick, children, testId, title }) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      onClick={onClick}
      aria-pressed={on}
      style={{
        padding: "3px 10px",
        border: `0.5px solid ${on ? DS.green : DS.borderHover}`,
        background: on ? "#E1F5EE" : DS.card,
        color: on ? DS.green : DS.textMuted,
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: DS.fontBody,
      }}
    >
      {on ? "☑ " : "☐ "}
      {children}
    </button>
  );
}

/**
 * Carte d'édition d'un envoi SMS.
 * @param {object} p
 * @param {Array<{id:string, nom:string}>} p.allLogs — logements (getAllLogsFlat)
 * @param {object|null} p.initial — ligne message_sends (messages joint) ou null
 * @param {() => void} p.onSaved
 * @param {() => void} p.onCancel
 */
export default function SmsSendCard({ allLogs, initial, onSaved, onCancel }) {
  const isMobile = useIsMobile();
  const isNew = !initial;
  const [s, setS] = useState(() => sendRowToDraft(initial));
  const [saving, setSaving] = useState(false);
  const [serverIssue, setServerIssue] = useState(null); // {errors, warnings}
  const [confirmDelete, setConfirmDelete] = useState(false);

  const logs = Array.isArray(allLogs) ? allLogs : [];

  const { state: condState, advanced: advancedConds } = useMemo(
    () => deriveSendConditionState(s.conditions),
    [s.conditions],
  );

  function update(patch) {
    setS((prev) => ({ ...prev, ...patch }));
    setServerIssue(null);
  }
  function setConditions(nextState) {
    update({ conditions: buildSendConditions(nextState, advancedConds) });
  }
  function togglePlatform(id) {
    const cur = condState.platforms;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setConditions({ ...condState, platforms: next });
  }
  function toggleLogement(id) {
    const cur = s.active_logement_ids || [];
    update({
      active_logement_ids: cur.includes(id)
        ? cur.filter((x) => x !== id)
        : [...cur, id],
    });
  }
  const allSelected =
    logs.length > 0 && (s.active_logement_ids || []).length === logs.length;

  function insertAtEnd(snippet) {
    const cur = s.body_fr || "";
    const sep = cur && !/\s$/.test(cur) ? " " : "";
    update({ body_fr: `${cur}${sep}${snippet}` });
  }

  // ── VALIDATION LIVE (confort — la vraie barrière est serveur) ─────────────
  const v = useMemo(() => validateSmsSendBody(s.body_fr), [s.body_fr]);
  // FAIL-CLOSED : `platforms` vide = « toutes les plateformes » (aucune
  // condition reservation.source n'est émise) ⇒ Airbnb est ciblé ⇒ un bloc
  // caution y serait supprimé et le SMS pourrait partir vide. Le motif exact
  // (aucune plateforme / Airbnb coché) choisit le message affiché — mêmes
  // règles et mêmes textes que le refus serveur (source unique validateur).
  const cautionReason = useMemo(
    () =>
      smsSendCautionAirbnbConflictReason({
        body: s.body_fr,
        platforms: condState.platforms,
      }),
    [s.body_fr, condState.platforms],
  );
  const liveErrors = useMemo(() => {
    const out = (s.body_fr || "").trim() === "" ? [] : [...v.errors];
    if (cautionReason) {
      out.push({
        code: "caution_airbnb_incompatible",
        message: smsSendCautionAirbnbMessage(cautionReason),
      });
    }
    return out;
  }, [v.errors, cautionReason, s.body_fr]);
  const bodyEmpty = (s.body_fr || "").trim() === "";
  const canSave = !saving && !bodyEmpty && liveErrors.length === 0;

  const isTemporal = String(s.trigger_type).startsWith("temporal_");
  const isFixedDate = s.trigger_type === "temporal_fixed_date";
  const { n: offsetN, direction: offsetDir } = splitOffset(
    s.trigger_offset_value,
  );

  const noLogement = (s.active_logement_ids || []).length === 0;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setServerIssue(null);
    try {
      await saveSmsSend({
        ...(s.send_id ? { send_id: s.send_id } : {}),
        name: s.name,
        body_fr: s.body_fr,
        trigger_type: s.trigger_type,
        trigger_offset_value: s.trigger_offset_value,
        trigger_offset_unit: s.trigger_offset_unit,
        trigger_time: s.trigger_time,
        trigger_fixed_date: s.trigger_fixed_date,
        conditions: s.conditions,
        active_logement_ids: s.active_logement_ids,
        status: s.status,
        catchup_mode: s.catchup_mode,
      });
      onSaved && onSaved();
    } catch (e) {
      const srv = e.server || {};
      if (srv.error === "validation_sms") {
        setServerIssue({
          errors: srv.errors || [],
          warnings: srv.warnings || [],
        });
      } else {
        setServerIssue({
          errors: [
            {
              code: srv.error || "save_failed",
              message: srv.detail || e.message || "Enregistrement échoué.",
            },
          ],
          warnings: [],
        });
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!s.send_id) return;
    setSaving(true);
    setServerIssue(null);
    try {
      await deleteSmsSend(s.send_id);
      onSaved && onSaved();
    } catch (e) {
      setServerIssue({
        errors: [
          {
            code: "delete_failed",
            message: e.message || "Suppression échouée.",
          },
        ],
        warnings: [],
      });
      setSaving(false);
    }
  }

  return (
    <div
      data-testid={isNew ? "sms-send-card-new" : `sms-send-card-${s.send_id}`}
      style={{
        border: `1px solid ${isNew ? DS.ciel : DS.border}`,
        borderRadius: 10,
        padding: 12,
        marginBottom: 8,
        background: DS.card,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11.5,
            fontWeight: 700,
            color: isNew ? DS.marine : DS.marineDark,
          }}
        >
          <MessageSquareText size={13} strokeWidth={2.2} />
          {isNew ? "Nouvel envoi SMS" : "Modifier l'envoi SMS"}
        </span>
        <span style={{ flex: 1 }} />
        {!isNew && !confirmDelete && (
          <button
            type="button"
            className="fbz-press"
            data-testid="sms-send-delete"
            onClick={() => setConfirmDelete(true)}
            disabled={saving}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: DS.card,
              color: DS.dangerText,
              border: `0.5px solid ${DS.borderHover}`,
              borderRadius: DS.radiusControl,
              padding: "5px 11px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            <Trash2 size={13} strokeWidth={2} /> Supprimer
          </button>
        )}
      </div>

      {confirmDelete && (
        <div
          data-testid="sms-send-delete-confirm"
          style={{
            border: `1px solid ${DS.danger}`,
            background: DS.dangerBg,
            borderRadius: DS.radiusControl,
            padding: "11px 13px",
            marginBottom: 10,
            fontSize: 12.5,
            color: DS.dangerText,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 9 }}>
            Supprimer cet envoi SMS et son texte ? Cette action est définitive.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="fbz-press"
              data-testid="sms-send-delete-yes"
              onClick={handleDelete}
              disabled={saving}
              style={{
                border: `1px solid ${DS.danger}`,
                background: DS.danger,
                color: "#fff",
                borderRadius: DS.radiusControl,
                padding: "7px 13px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              Oui, supprimer
            </button>
            <button
              type="button"
              className="fbz-press"
              onClick={() => setConfirmDelete(false)}
              style={{
                border: `1px solid ${DS.border}`,
                background: DS.card,
                color: DS.textSecondary,
                borderRadius: DS.radiusControl,
                padding: "7px 13px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ── 1. TEXTE DU SMS ────────────────────────────────────────────── */}
      <div style={{ marginBottom: 12 }}>
        <BlockTitle>Le texte du SMS</BlockTitle>
        <input
          data-testid="sms-send-name"
          value={s.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Nom interne (ex. SMS instructions veille 20h)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 8,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: 7,
            padding: "6px 10px",
            fontSize: 12,
            background: DS.card,
            fontFamily: DS.fontBody,
          }}
        />
        <textarea
          data-testid="sms-send-body"
          value={s.body_fr}
          onChange={(e) => update({ body_fr: e.target.value })}
          rows={4}
          placeholder="Écrivez le SMS ; utilisez « Insérer » pour le prénom ou le lien"
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: 13,
            lineHeight: 1.5,
            fontFamily: DS.fontBody,
            color: DS.textPrimary,
            padding: "10px 12px",
            borderRadius: DS.radiusControl,
            border:
              liveErrors.length > 0
                ? `1px solid ${DS.danger}`
                : `0.5px solid ${DS.border}`,
            background: DS.card,
            resize: "vertical",
          }}
        />
        <SmsSendWorstCase body={s.body_fr} testId="sms-send-worstcase" />

        <div
          style={{
            display: "flex",
            gap: 5,
            flexWrap: "wrap",
            marginTop: 8,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 10.5, color: DS.textMuted, fontWeight: 700 }}>
            Insérer :
          </span>
          {SMS_SEND_RULES.vars.map((name) => (
            <button
              key={name}
              type="button"
              data-testid={`sms-send-insert-var-${name}`}
              onClick={() => insertAtEnd(`{${name}}`)}
              style={{
                border: `0.5px dashed ${DS.marine}`,
                background: "transparent",
                color: DS.marine,
                borderRadius: 7,
                padding: "3px 9px",
                fontSize: 10.5,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              {VAR_LABELS[name] || name}
            </button>
          ))}
          {[...SMS_SEND_RULES.conditions].map((cond) => (
            <button
              key={cond}
              type="button"
              data-testid={`sms-send-insert-if-${cond}`}
              onClick={() => insertAtEnd(`{#if ${cond}}texte{/if}`)}
              style={{
                border: `0.5px dashed ${DS.borderHover}`,
                background: "transparent",
                color: DS.textSecondary,
                borderRadius: 7,
                padding: "3px 9px",
                fontSize: 10.5,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              {CONDITION_LABELS[cond] || cond}
            </button>
          ))}
        </div>

        <Feedback
          errors={liveErrors}
          warnings={bodyEmpty ? [] : v.warnings}
          testId="sms-send-feedback"
        />
        {serverIssue && (
          <Feedback
            errors={serverIssue.errors}
            warnings={serverIssue.warnings}
            testId="sms-send-server-feedback"
          />
        )}

        {/* Traductions EN/ES : générées SERVEUR à l'enregistrement, lecture
            seule (même contrat que l'éditeur des gabarits SMS). Une langue
            absente = le moteur retombe sur le français. */}
        {!isNew && initial && initial.messages && (
          <div
            data-testid="sms-send-translations"
            style={{
              marginTop: 10,
              fontSize: 11,
              color: DS.textMuted,
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 700, color: DS.marine, marginBottom: 3 }}>
              Traductions générées à l'enregistrement (lecture seule)
            </div>
            <div>
              EN :{" "}
              {initial.messages.body_en || "— indisponible, repli sur le français"}
            </div>
            <div>
              ES :{" "}
              {initial.messages.body_es || "— indisponible, repli sur le français"}
            </div>
          </div>
        )}
      </div>

      {/* ── 2. QUAND ───────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 12 }}>
        <BlockTitle>Quand envoyer ?</BlockTitle>
        <select
          data-testid="sms-send-trigger"
          value={s.trigger_type}
          onChange={(e) => {
            const id = e.target.value;
            update(
              id.startsWith("event_")
                ? { trigger_type: id, trigger_offset_value: 0 }
                : { trigger_type: id },
            );
          }}
          style={{
            width: "100%",
            marginBottom: 8,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 12.5,
            fontWeight: 600,
            color: DS.marineDark,
            background: DS.card,
            fontFamily: DS.fontBody,
          }}
        >
          {TRIGGER_TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>

        {isTemporal && !isFixedDate && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
              background: DS.pageBg,
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "9px 12px",
              fontSize: 12.5,
              color: DS.textSecondary,
            }}
          >
            <span>Envoyer</span>
            <input
              type="number"
              min={0}
              data-testid="sms-send-offset-n"
              aria-label="Nombre"
              value={offsetN}
              onChange={(e) =>
                update({
                  trigger_offset_value: joinOffset(
                    parseInt(e.target.value, 10) || 0,
                    offsetDir,
                  ),
                })
              }
              style={{
                width: 56,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            />
            <select
              aria-label="Unité"
              data-testid="sms-send-offset-unit"
              value={s.trigger_offset_unit || "days"}
              onChange={(e) => update({ trigger_offset_unit: e.target.value })}
              style={{
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            >
              <option value="minutes">{OFFSET_UNIT_LABEL.minutes}</option>
              <option value="hours">{OFFSET_UNIT_LABEL.hours}</option>
              <option value="days">{OFFSET_UNIT_LABEL.days}</option>
            </select>
            {offsetN === 0 ? (
              <span style={{ fontWeight: 600, color: DS.marine }}>
                le jour même de {TEMPORAL_ANCHOR_PHRASE[s.trigger_type]}
              </span>
            ) : (
              <>
                <span style={{ display: "inline-flex", gap: 2 }}>
                  {["before", "after"].map((d) => {
                    const on = offsetDir === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        data-testid={`sms-send-offset-${d}`}
                        onClick={() =>
                          update({
                            trigger_offset_value: joinOffset(offsetN, d),
                          })
                        }
                        aria-pressed={on}
                        style={{
                          padding: "3px 10px",
                          border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                          borderRadius: 7,
                          background: on ? DS.marine : DS.card,
                          color: on ? "#fff" : DS.textSecondary,
                          fontSize: 12,
                          fontWeight: on ? 700 : 500,
                          cursor: "pointer",
                        }}
                      >
                        {d === "before" ? "avant" : "après"}
                      </button>
                    );
                  })}
                </span>
                <span>{TEMPORAL_ANCHOR_PHRASE[s.trigger_type]}</span>
              </>
            )}
            <span>, à</span>
            <input
              data-testid="sms-send-time"
              aria-label="Heure"
              value={s.trigger_time || ""}
              onChange={(e) => update({ trigger_time: e.target.value })}
              placeholder="20:00"
              style={{
                width: 62,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            />
            <span style={{ fontSize: 11, color: DS.textMuted }}>
              (heure de Paris)
            </span>
          </div>
        )}

        {isFixedDate && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              background: DS.pageBg,
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "9px 12px",
              fontSize: 12.5,
              color: DS.textSecondary,
            }}
          >
            <span>Le</span>
            <input
              type="date"
              data-testid="sms-send-fixed-date"
              aria-label="Date"
              value={s.trigger_fixed_date || ""}
              onChange={(e) => update({ trigger_fixed_date: e.target.value })}
              style={{
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                background: DS.card,
              }}
            />
            <span>à</span>
            <input
              data-testid="sms-send-time"
              aria-label="Heure"
              value={s.trigger_time || ""}
              onChange={(e) => update({ trigger_time: e.target.value })}
              placeholder="20:00"
              style={{
                width: 62,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            />
          </div>
        )}
      </div>

      {/* ── 3. PLATEFORME + 4. SEULEMENT SI ─────────────────────────────── */}
      <div style={{ marginBottom: 12 }}>
        <BlockTitle>
          Seulement si{" "}
          <span
            style={{
              fontWeight: 400,
              textTransform: "none",
              color: DS.textMuted,
            }}
          >
            (rien coché = toutes les réservations Plateformes, Airbnb compris)
          </span>
        </BlockTitle>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 8,
          }}
        >
          <div
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "8px 10px",
              background: DS.pageBg,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: DS.textMuted,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Plateforme
            </div>
            <div
              data-testid="sms-send-platforms"
              style={{ display: "flex", flexWrap: "wrap", gap: 5 }}
            >
              {/* ADR-266 STRICT : « Direct » n'est JAMAIS rendu ici — les résas
                  directes ont leur propre parcours (onglet Direct). */}
              {PLATFORM_OPTIONS.filter((o) => o.id !== "direct").map((o) => (
                <Chip
                  key={o.id}
                  testId={`sms-send-platform-${o.id}`}
                  on={condState.platforms.includes(o.id)}
                  onClick={() => togglePlatform(o.id)}
                >
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>

          {[
            ["sejourPaye", "Séjour payé", ["Peu importe", "Payé", "Non payé"]],
            ["cautionRecue", "Caution reçue", ["Peu importe", "Reçue", "Non reçue"]],
            [
              "formulaireRempli",
              "Formulaire de bienvenue",
              ["Peu importe", "Rempli", "Non rempli"],
            ],
          ].map(([key, title, labels]) => (
            <div
              key={key}
              style={{
                border: `0.5px solid ${DS.border}`,
                borderRadius: 9,
                padding: "8px 10px",
                background: DS.pageBg,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: DS.textMuted,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                {title}
              </div>
              <div
                data-testid={`sms-send-tristate-${key}`}
                style={{ display: "flex", gap: 3 }}
              >
                {["any", "yes", "no"].map((val, i) => {
                  const on = condState[key] === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setConditions({ ...condState, [key]: val })}
                      aria-pressed={on}
                      style={{
                        flex: 1,
                        padding: "3px 4px",
                        border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                        background: on ? DS.marine : DS.card,
                        color: on ? "#fff" : DS.textMuted,
                        borderRadius: 6,
                        fontSize: 10,
                        fontWeight: on ? 700 : 500,
                        cursor: "pointer",
                      }}
                    >
                      {labels[i]}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 5. LOGEMENTS ───────────────────────────────────────────────── */}
      <div style={{ marginBottom: 12 }}>
        <BlockTitle>Pour quels logements ?</BlockTitle>
        <div
          data-testid="sms-send-logements"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 5,
            padding: "6px 8px",
            background: DS.pageBg,
            borderRadius: 8,
            maxHeight: 110,
            overflowY: "auto",
          }}
        >
          <button
            type="button"
            data-testid="sms-send-logement-all"
            onClick={() =>
              update({
                active_logement_ids: allSelected ? [] : logs.map((l) => l.id),
              })
            }
            aria-pressed={allSelected}
            style={{
              padding: "4px 10px",
              border: `0.5px solid ${allSelected ? DS.marine : DS.borderHover}`,
              background: allSelected ? DS.marine : DS.card,
              color: allSelected ? "#fff" : DS.textSecondary,
              borderRadius: 7,
              fontSize: 10.5,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {allSelected ? "☑ " : "☐ "}Tous
          </button>
          {logs.map((l) => (
            <Chip
              key={l.id}
              testId={`sms-send-logement-${l.id}`}
              on={(s.active_logement_ids || []).includes(l.id)}
              onClick={() => toggleLogement(l.id)}
            >
              {l.nom}
            </Chip>
          ))}
        </div>
        {noLogement && (
          <div
            data-testid="sms-send-no-logement"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 6,
              fontSize: 11,
              color: DS.dangerText,
              fontWeight: 700,
            }}
          >
            <AlertTriangle size={13} strokeWidth={2} /> Aucun logement coché =
            cet envoi ne part jamais.
          </div>
        )}
      </div>

      {/* ── 6. STATUT ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 10 }}>
        <BlockTitle>Statut de l'envoi</BlockTitle>
        <select
          data-testid="sms-send-status"
          value={sendStatusOptionKey(s.status, s.catchup_mode)}
          onChange={(e) =>
            update(sendStatusOptionPatch(e.target.value, s.catchup_mode))
          }
          style={{
            width: "100%",
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: 7,
            padding: "6px 10px",
            fontSize: 12,
            background: DS.card,
            fontFamily: DS.fontBody,
          }}
        >
          {SEND_STATUS_OPTIONS.map((o) => (
            <option key={o.key} value={o.key} title={o.help}>
              {o.label}
            </option>
          ))}
        </select>
        <div
          data-testid="sms-send-status-help"
          style={{
            fontSize: 11,
            color: DS.textMuted,
            marginTop: 5,
            lineHeight: 1.4,
          }}
        >
          {sendStatusHelp(s.status, s.catchup_mode)}
        </div>
        {/* D3 — un SMS d'instructions doit rester rattrapable. */}
        {s.catchup_mode === "reminder" && (
          <div
            data-testid="sms-send-reminder-warning"
            style={{
              marginTop: 6,
              fontSize: 11,
              color: DS.warningText,
              fontWeight: 600,
              lineHeight: 1.45,
            }}
          >
            En « Rappel », une réservation créée après l'heure prévue ne reçoit
            RIEN. Pour un SMS d'instructions d'arrivée, garde « Essentiel ».
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          paddingTop: 8,
          borderTop: `0.5px solid ${DS.border}`,
        }}
      >
        <button
          type="button"
          className="fbz-press"
          data-testid="sms-send-cancel"
          onClick={onCancel}
          disabled={saving}
          style={{
            background: DS.card,
            color: DS.textSecondary,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          className="fbz-press"
          data-testid="sms-send-save"
          onClick={handleSave}
          disabled={!canSave}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "6px 16px",
            fontSize: 12,
            fontWeight: 700,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.5,
            fontFamily: DS.fontBody,
          }}
        >
          {isNew ? (
            <Plus size={13} strokeWidth={2.4} />
          ) : (
            <Save size={13} strokeWidth={2} />
          )}
          {saving ? "…" : isNew ? "Créer" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}
